import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  UsersIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import {
  ExclamationTriangleIcon as ExclamationTriangleSolid
} from '@heroicons/react/24/solid';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { formatNumber, formatDate } from '../../../utils/formatting';
import useValidations from '../../../hooks/useValidations';
import { useBilling } from '../../../contexts/BillingContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getSession } from '../../../api/supabase';
import { apiRequest } from '../../../utils/apiUtils';

const TableInsights = ({ tableName, connectionId }) => {
  // Check if profiling (Tier 4) is enabled for this plan
  const { isFeatureEnabled, plan, getUpgradeUrl } = useBilling();
  const enabledTiers = plan?.features?.enabled_tiers || [1, 2];
  const profilingEnabled = enabledTiers.includes(4) || isFeatureEnabled('tier4_enabled');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState(null);

  // Get validation data for health score
  const {
    validations: validationResults,
    loading: validationsLoading,
  } = useValidations(connectionId, tableName);

  // Simple MVP data structure - this would come from real APIs
  const [insights, setInsights] = useState({
    usage: {
      queriesLast7Days: 42,
      uniqueUsers: 5,
      lastAccessed: '2025-08-12T14:30:00Z',
      profileTrends: null
    }
  });

  useEffect(() => {
    const fetchUsageData = async () => {
      if (!connectionId || !tableName) return;
      
      try {
        setLoading(true);

        const data = await apiRequest(`/tables/${encodeURIComponent(tableName)}/usage?connection_id=${connectionId}`, {
          method: 'GET'
        });

        setInsights(prev => ({
          ...prev,
          usage: data
        }));
      } catch (error) {
        console.error('Error fetching usage data:', error);
        // Keep mock data on error
      } finally {
        setLoading(false);
      }
    };

    fetchUsageData();
  }, [connectionId, tableName]);

  if (loading) {
    return (
      <div className="text-center py-16">
        <LoadingSpinner size="lg" className="mx-auto" />
        <p className="mt-4 text-sm text-gray-500">Loading insights for {tableName}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading insights</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  // Calculate health score from validations
  const validationRules = validationResults || [];
  const passedValidations = validationRules.filter(r => r.last_result === true).length;
  const failedValidations = validationRules.filter(r => r.last_result === false).length;
  const totalValidations = validationRules.length;
  const healthScore = totalValidations > 0 ? 
    Math.round((passedValidations / totalValidations) * 100) : 
    85; // Default score if no validations

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Table Usage & Health</h2>
        <p className="text-gray-600">Basic insights and activity for {tableName}</p>
      </div>

      {/* Simple Metrics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Recent Queries */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
                <ChartBarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Queries (7 days)</dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {formatNumber(insights.usage.queriesLast7Days)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Active Users */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                <UsersIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Unique Users</dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {formatNumber(insights.usage.uniqueUsers)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Last Accessed */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
                <ClockIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Last Accessed</dt>
                  <dd className="text-sm font-semibold text-gray-900">
                    {formatDate(insights.usage.lastAccessed, true)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Health Score */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className={`flex-shrink-0 rounded-md p-3 ${
                healthScore >= 80 ? 'bg-green-100' : 
                healthScore >= 60 ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                <InformationCircleIcon className={`h-6 w-6 ${
                  healthScore >= 80 ? 'text-green-600' : 
                  healthScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                }`} />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Health Score</dt>
                  <dd className={`text-2xl font-semibold ${
                    healthScore >= 80 ? 'text-green-600' : 
                    healthScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {healthScore}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile History Trends */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Data Evolution</h3>
          <EyeIcon className="h-5 w-5 text-gray-400" />
        </div>
        
        {insights.usage.profileTrends && insights.usage.profileTrends.timestamps.length > 0 ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(() => {
                const trends = insights.usage.profileTrends;
                const rowCounts = trends.rowCounts;
                const latestRows = rowCounts[rowCounts.length - 1];
                const previousRows = rowCounts.length > 1 ? rowCounts[rowCounts.length - 2] : latestRows;
                const rowChange = latestRows - previousRows;
                const rowChangePercent = previousRows > 0 ? ((rowChange / previousRows) * 100).toFixed(1) : 0;
                
                return (
                  <>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-2xl font-bold text-blue-700">{formatNumber(latestRows)}</div>
                          <div className="text-sm text-blue-600">Current Rows</div>
                        </div>
                        <div className="flex items-center">
                          {rowChange > 0 ? (
                            <ArrowTrendingUpIcon className="h-5 w-5 text-green-500 mr-1" />
                          ) : rowChange < 0 ? (
                            <ArrowTrendingDownIcon className="h-5 w-5 text-red-500 mr-1" />
                          ) : null}
                          <span className={`text-sm font-medium ${
                            rowChange > 0 ? 'text-green-600' : rowChange < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {rowChange > 0 ? '+' : ''}{rowChangePercent}%
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <div className="text-2xl font-bold text-purple-700">
                        {trends.columnCounts[trends.columnCounts.length - 1]}
                      </div>
                      <div className="text-sm text-purple-600">Columns</div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Charts - Split into two separate charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Row Count Chart */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-3">Row Count Evolution</h4>
                <div style={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <LineChart data={(() => {
                      const trends = insights.usage.profileTrends;
                      return trends.timestamps.map((timestamp, index) => ({
                        date: new Date(timestamp).toLocaleDateString(),
                        rowCount: trends.rowCounts[index]
                      }));
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DBEAFE" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        labelFormatter={(value) => `Date: ${value}`}
                        formatter={(value) => [formatNumber(value), 'Row Count']}
                        contentStyle={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="rowCount" 
                        stroke="#3B82F6" 
                        strokeWidth={3}
                        dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: '#1E40AF', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Column Count Chart */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <h4 className="text-sm font-medium text-purple-800 mb-3">Column Count Evolution</h4>
                <div style={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <LineChart data={(() => {
                      const trends = insights.usage.profileTrends;
                      return trends.timestamps.map((timestamp, index) => ({
                        date: new Date(timestamp).toLocaleDateString(),
                        columns: trends.columnCounts[index]
                      }));
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDE9FE" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        labelFormatter={(value) => `Date: ${value}`}
                        formatter={(value) => [value, 'Column Count']}
                        contentStyle={{ backgroundColor: '#F3F4F6', border: '1px solid #D1D5DB' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="columns" 
                        stroke="#8B5CF6" 
                        strokeWidth={3}
                        dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: '#6D28D9', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            {profilingEnabled ? (
              <>
                <div className="text-gray-500 mb-2">No profile history available</div>
                <div className="text-sm text-gray-400">
                  Run table profiling multiple times to see data evolution trends
                </div>
              </>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
                <div className="flex items-start">
                  <LockClosedIcon className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <h4 className="text-sm font-medium text-amber-800 mb-1">Data Evolution Trends Available on Starter Plan</h4>
                    <p className="text-sm text-amber-700 mb-3">
                      Upgrade to track how your table data changes over time with profiling history.
                    </p>
                    <Link
                      to={getUpgradeUrl ? getUpgradeUrl() : '/settings/billing'}
                      className="inline-flex items-center px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700"
                    >
                      <LockClosedIcon className="h-4 w-4 mr-1.5" />
                      Upgrade to Unlock
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation Summary */}
      {totalValidations > 0 && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Data Quality Summary</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="text-2xl font-bold text-green-700">{passedValidations}</div>
              <div className="text-sm text-green-600">Passing Validations</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="text-2xl font-bold text-red-700">{failedValidations}</div>
              <div className="text-sm text-red-600">Failing Validations</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-2xl font-bold text-gray-700">{totalValidations}</div>
              <div className="text-sm text-gray-600">Total Validations</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableInsights;